import { galText } from "./accounts";

export const MAX_GAL = 10_000;

export type Gallons = { raw: number; scrap: number };

export type GallonsField = "total" | "scrap";

const amount = (text: string) => {
  const value = text.trim().replace(/,/g, "");
  return /^\d+(?:\.\d+)?$/.test(value) ? Number(value) : null;
};

/** Rule P-3 as the driver types: a text when the scrap is more than the total, else "". */
export function scrapOverText(total: string, scrap: string, word = "gallons"): string {
  const raw = amount(total);
  const less = amount(scrap);
  return raw !== null && less !== null && less > raw ? `Scrap ${word} cannot be more than total ${word}.` : "";
}

export const SCRAP_EMPTY = "Type the scrap gallons. Type 0 if there is no scrap.";

/** The Total gallons and Scrap gallons fields of one pickup (D-17). Both need a number; 0 is a number. */
export function parseGallons(total: string, scrap: string): Gallons | { field: GallonsField; error: string } {
  if (!total.trim()) return { field: "total", error: "Type the total gallons." };
  const raw = amount(total);
  if (raw === null) return { field: "total", error: "Type the total gallons as a number, for example 42." };
  if (raw > MAX_GAL) return { field: "total", error: `Total gallons cannot be more than ${MAX_GAL.toLocaleString("en-US")}.` };
  if (!scrap.trim()) return { field: "scrap", error: SCRAP_EMPTY };
  const less = amount(scrap);
  if (less === null) return { field: "scrap", error: "Type the scrap gallons as a number, for example 9." };
  if (less > raw) return { field: "scrap", error: scrapOverText(total, scrap) };
  return { raw, scrap: less };
}

/** Rule D-17: a pickup with gallons and a scrap of 0 needs a confirm before the save. */
export const asksNoScrap = (raw: number | null | undefined, scrap: number | null | undefined) => (raw ?? 0) > 0 && scrap === 0;

export const NO_SCRAP_TEXT = "Scrap is 0. Only about 1 pickup in 400 has no scrap. Is there no scrap at this pickup?";

export const gallonsText = (gallons: Gallons) => `${gallons.raw} gal total, ${gallons.scrap} gal scrap`;

/** The calendar date in the time zone of the organization, as YYYY-MM-DD. */
export function todayIn(timeZone: string, now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

export function dateError(day: string, today: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return "Enter the date of the pickup.";
  if (day > today) return "The pickup date cannot be after today.";
  const first = new Date(`${today}T12:00:00Z`);
  first.setUTCDate(first.getUTCDate() - 365);
  if (day < first.toISOString().slice(0, 10)) return "The pickup date must be in the last 365 days.";
  return "";
}

/** Raw gallons above the container capacity need a second press of Save. */
export function capacityWarning(gallons: Gallons, capacity: number): string {
  if (capacity <= 0 || gallons.raw <= capacity) return "";
  return `${galText(gallons.raw)} is more than the ${galText(capacity)} of the containers. Press Save again to save it.`;
}

/** With no container size, the truck size is the limit, as in the load check. */
export function truckWarning(gallons: Gallons, truck: { code: string; capacity_gal?: number } | undefined): string {
  const size = truck?.capacity_gal ?? 0;
  if (!truck || size <= 0 || gallons.raw <= size) return "";
  return `${galText(gallons.raw)} is more than the ${galText(size)} of truck ${truck.code}. Press Save again to save it.`;
}

/** The gallons that a second Save confirms over the container size. */
export const capacityKey = (gallons: Gallons) => `${gallons.raw} ${gallons.scrap}`;
